// Payment-related filters (transaction_summary, payment_methods)

import { PAYMENT_ORIGIN_GROUPS } from '@supply-mover-2-0/shared';
import {
	createDateRangeFilter,
	createKeywordFilter,
	createStatusFilter,
} from './factories';
import type { FilterOption, FilterRegistry } from './types';

/**
 * PD-17378 — the customer-facing origin groups (Storefront, Payment link,
 * Staff, Delivery, Autopay / scheduled, ERP, Other) as filter options. The
 * Transactions list and Cash Receipts offer the same groups, defined once in
 * libs/shared payment-origin.ts.
 */
const PAYMENT_ORIGIN_OPTIONS: Array<FilterOption> = PAYMENT_ORIGIN_GROUPS.map(
	(group) => ({ id: group.key, label: group.label }),
);

export const paymentFilters: FilterRegistry = {
	transactionKeywords: {
		...createKeywordFilter({
			label: 'Search Transactions',
			placeholder:
				'Search by ID (internal / ERP / processor), client, method...',
			objectTable: 'transaction_summary',
			listKeys: ['transaction_summary', 'transaction-client-embedded'],
			fulltextFields: [
				'_id',
				'm_payment_externalId_erp',
				'm_payment_externalId_paymentProcessor',
				'clientName',
				'clientExternalId',
				'clientAddress',
				'clientCity',
				'clientState',
				'm_payment_payment_method_name',
				'm_payment_payment_method_type',
				'referenceNumber',
				'invoiceId',
				'orderId',
			],
		}),
		queryParamName: 'transactionKeywords',
	},

	paymentDateRange: {
		...createDateRangeFilter({
			label: 'Payment Date',
			objectTable: 'transaction_summary',
			dateField: 'm_payment_datetime',
			// m_payment_datetime lives on the joined m_payment_transaction
			// (alias `transaction`), not on transaction_summary (alias `summary`).
			columnAlias: 'transaction',
			listKeys: ['transaction_summary', 'transaction-client-embedded'],
		}),
		queryParamName: 'paymentDateRange',
	},

	transactionStatus: {
		...createStatusFilter({
			label: 'Payment Status',
			options: [
				{ id: 'Processing', label: 'Processing' },
				{ id: 'Processing refund', label: 'Processing Refund' },
				{ id: 'Partially refunded', label: 'Partially Refunded' },
				{ id: 'Refunded', label: 'Refunded' },
				{ id: 'Voided', label: 'Voided' },
				{ id: 'Succeeded', label: 'Succeeded' },
				{ id: 'Failed', label: 'Failed' },
			],
			objectTable: 'transaction_summary',
			fieldName: 'm_payment_status',
			listKeys: ['transaction_summary', 'transaction-client-embedded'],
			colorScheme: 'orange',
		}),
		queryParamName: 'transactionStatus',
	},

	/**
	 * PD-17378 — the origin groups, not the stored codes. The options used to
	 * be five raw codes (CRM, Ecommerce, ERP, External, Automated): payment
	 * links, delivery and scheduled payments could not be picked at all, and
	 * nothing writes External. The filter builder's `transactionOrigin` case
	 * maps each group to its codes, and still reads an old saved code as its
	 * group.
	 */
	transactionOrigin: {
		...createStatusFilter({
			label: 'Origin',
			options: PAYMENT_ORIGIN_OPTIONS,
			objectTable: 'transaction_summary',
			fieldName: 'm_payment_origin',
			// m_payment_origin lives on the joined `transaction` alias: the
			// chain's origin transaction, the same row Cash Receipts groups by.
			columnAlias: 'transaction',
			listKeys: ['transaction_summary', 'transaction-client-embedded'],
			colorScheme: 'purple',
		}),
		queryMapping: {
			objectTable: 'transaction_summary',
			fieldName: 'm_payment_origin',
			columnAlias: 'transaction',
			joinType: 'custom',
		},
		queryParamName: 'transactionOrigin',
	},

	/**
	 * PD-17378 — Cash Receipts' Payment origin filter: the same groups, sent
	 * as group keys. The report service applies it in its own SQL (on the
	 * payment chain's origin), so it has no list query mapping.
	 */
	paymentOrigin: {
		type: 'multi-select',
		label: 'Payment origin',
		placeholder: 'Select payment origin...',
		colorScheme: 'purple',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		options: PAYMENT_ORIGIN_OPTIONS,
		queryParamName: 'paymentOrigin',
		multiple: true,
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		// Report-only, like reconciliationFlags: no query mapping, so no list
		// builder applies it, and `reports` keeps it out of List Configuration.
		listKeys: ['reports'],
	},

	transactionType: {
		...createStatusFilter({
			label: 'Transaction Type',
			options: [
				{ id: 'Authorization', label: 'Authorization' },
				{ id: 'Capture', label: 'Capture' },
				{ id: 'Sale', label: 'Sale' },
				{ id: 'Payment', label: 'Payment' },
				{ id: 'Refund', label: 'Refund' },
				{ id: 'Void', label: 'Void' },
			],
			objectTable: 'transaction_summary',
			// The transaction type column is `m_payment_type` on the joined
			// `transaction` alias. (Previously `fieldName: 'transactionType'`, which
			// matched no column — the filter was silently inert before the migration.)
			fieldName: 'm_payment_type',
			columnAlias: 'transaction',
			listKeys: ['transaction_summary'],
			colorScheme: 'green',
		}),
		/**
		 * PD-15117 — match on the payment CHAIN, not on its origin transaction.
		 *
		 * Testing `transaction.m_payment_type` only ever saw the summary's origin,
		 * and a refund or a void is never an origin: it is a child that folds into
		 * the summary of the payment it reversed. So Refund and Void could not
		 * match anything, on any tenant, while real refunds existed.
		 *
		 * `m_payment_fk_m_payment_transaction__id_origin` is self-referential on an
		 * origin row, so this EXISTS covers the origin and its children alike —
		 * Authorization / Capture / Sale keep matching exactly as before. A chain
		 * whose only refund attempt FAILED still matches Refund: a declined refund
		 * is a refund transaction, and the list already carries a failed-attempt
		 * count column (PD-15091) to tell the two apart.
		 */
		queryMapping: {
			objectTable: 'transaction_summary',
			joinType: 'exists',
			joinTable: 'm_payment_transaction',
			joinConditions: {
				sourceField: 'm_payment_fk_m_payment_transaction__id_origin',
				targetField: 'm_payment_fk_m_payment_transaction__id_origin',
				filterField: 'm_payment_type',
			},
		},
		queryParamName: 'transactionType',
	},

	/**
	 * PD-14977 — money held vs money actually taken.
	 *
	 * An authorization is written unsettled and a capture settled, so this is
	 * the one field that separates a hold on a customer's card from a charge
	 * against it. It could not be filtered at all before: the column was never
	 * selected into the list query and never returned.
	 *
	 * Same column-alias caveat as transactionType above — the flag lives on
	 * the joined `transaction` alias, not on the summary row.
	 */
	transactionFundsCollected: {
		...createStatusFilter({
			label: 'Settlement',
			// Option ids are '1'/'0', NOT 'true'/'false'. The id goes into SQL
			// against a tinyint(1), and MySQL coerces a non-numeric string to 0
			// — so 'true' and 'false' both become 0 and the filter returns the
			// unsettled rows whichever option you pick. Verified: both values
			// returned an identical 18 rows before this was corrected.
			options: [
				{ id: '1', label: 'Settled' },
				{ id: '0', label: 'Pending settlement' },
			],
			objectTable: 'transaction_summary',
			fieldName: 'm_payment_isSettled',
			columnAlias: 'transaction',
			listKeys: ['transaction_summary'],
			colorScheme: 'orange',
		}),
		queryParamName: 'transactionFundsCollected',
	},

	/**
	 * PD-15012 — filter transactions by how they were paid. Options are the
	 * platform's payment-method types; a tenant that never uses one simply
	 * gets no rows for it.
	 */
	transactionPaymentMethod: {
		...createStatusFilter({
			label: 'Payment Method',
			options: [
				{ id: 'Credit Card', label: 'Credit Card' },
				{ id: 'Credit Card Reader', label: 'Credit Card Reader' },
				{ id: 'ACH', label: 'ACH' },
				{ id: 'Cash', label: 'Cash' },
				{ id: 'Check', label: 'Check' },
				{ id: 'Account Funds', label: 'Account Funds' },
				{ id: 'Company Credit', label: 'Company Credit' },
			],
			objectTable: 'transaction_summary',
			fieldName: 'm_payment_payment_method_type',
			// The method type lives on the joined `transaction` alias.
			columnAlias: 'transaction',
			listKeys: ['transaction_summary', 'transaction-client-embedded'],
			colorScheme: 'blue',
		}),
		queryParamName: 'transactionPaymentMethod',
	},

	/**
	 * PD-15012 — filter card transactions by their stored card brand
	 * (m_payment_payment_method_subtype). The subtype column also carries
	 * bank-account types for ACH ('Checking'), which are deliberately not
	 * offered here — this filter answers "show me all the Visa charges".
	 */
	transactionCardType: {
		...createStatusFilter({
			label: 'Card Type',
			options: [
				{ id: 'Visa', label: 'Visa' },
				{ id: 'MasterCard', label: 'MasterCard' },
				{ id: 'Amex', label: 'Amex' },
				{ id: 'Discover', label: 'Discover' },
			],
			objectTable: 'transaction_summary',
			fieldName: 'm_payment_payment_method_subtype',
			// The subtype lives on the joined `transaction` alias.
			columnAlias: 'transaction',
			listKeys: ['transaction_summary', 'transaction-client-embedded'],
			colorScheme: 'purple',
		}),
		queryParamName: 'transactionCardType',
	},

	paymentMethodKeywords: {
		...createKeywordFilter({
			label: 'Search Payment Methods',
			placeholder: 'Search by last 4 digits, name, routing number...',
			objectTable: '_client_payment_method',
			listKeys: ['payment_methods'],
			fulltextFields: [
				'paymentLastFour',
				'paymentName',
				'paymentRoutingNumber',
			],
		}),
		queryParamName: 'paymentMethodKeywords',
	},

	paymentMethodType: {
		...createStatusFilter({
			label: 'Payment Type',
			options: [
				{ id: 'ACH', label: 'ACH' },
				{ id: 'Credit Card', label: 'Credit Card' },
			],
			objectTable: '_client_payment_method',
			fieldName: 'paymentType',
			listKeys: ['payment_methods'],
		}),
		queryParamName: 'paymentMethodType',
	},

	transactionSalesRep: {
		type: 'multi-select',
		label: 'Sales Rep',
		placeholder: 'Select sales rep...',
		colorScheme: 'blue',
		searchable: true,
		loadOnFocus: true,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'display_name' as keyof unknown,
		},
		apiEndpoint: '/users/salespeople',
		queryParamName: 'transactionSalesRep',
		multiple: true,
		options: [{ id: 'ME', label: 'Me' }],
		queryMapping: {
			objectTable: 'transaction_summary',
			fieldName: 'clientSalesRepId',
			// Sales rep is matched against the joined `client` alias's
			// _fk_user__id / _fk_user__id_alternate columns by the dedicated
			// `transactionSalesRep` handler in the filter builder's custom case.
			columnAlias: 'client',
			joinType: 'custom',
		},
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['transaction_summary'],
	},
};
